import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  UserSettings,
  NotificationPreference,
  NotificationEventType,
} from '@command-floor/shared-types';
import { api } from '../lib/api.js';

const EVENT_LABELS: Record<NotificationEventType, string> = {
  FLAG_RAISED_MY_TABLE: 'Flag raised on my work effort',
  FLAG_RAISED_MY_ENTITY: 'Flag raised in my entity',
  FLAG_RAISED_ANY: 'Flag raised anywhere (Leadership)',
  FLAG_LOWERED_MY_TABLE: 'Flag lowered on my work effort',
  ACTIVITY_ASSIGNED_TO_ME: 'Activity assigned to me',
  ACTIVITY_COMPLETED_MINE_BY_OTHER: 'My activity completed by someone else',
  ACTIVITY_REASSIGNED_MINE: 'My activity reassigned',
  NOTE_ADDED_MY_TABLE: 'Note added to my work effort',
  TABLE_REVIEWED_MY_TABLE: 'My work effort marked as reviewed',
};

export function SettingsPage() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['me-settings'],
    queryFn: () => api.get<UserSettings>('/api/me/settings'),
  });

  const [notifEmail, setNotifEmail] = useState('');
  const [quietStart, setQuietStart] = useState('');
  const [quietEnd, setQuietEnd] = useState('');
  const [digestMode, setDigestMode] = useState(false);
  const [timeZone, setTimeZone] = useState('America/Chicago');
  const [prefs, setPrefs] = useState<NotificationPreference[]>([]);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setNotifEmail(data.notification_email ?? '');
    setQuietStart(data.quiet_hours_start ?? '');
    setQuietEnd(data.quiet_hours_end ?? '');
    setDigestMode(data.digest_mode);
    setTimeZone(data.time_zone);
    setPrefs(data.preferences);
  }, [data]);

  const save = useMutation({
    mutationFn: () =>
      api.patch('/api/me/settings', {
        notification_email: notifEmail || null,
        quiet_hours_start: quietStart || null,
        quiet_hours_end: quietEnd || null,
        digest_mode: digestMode,
        time_zone: timeZone,
        preferences: prefs,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me-settings'] });
      setSaved(true);
      setSaveError(null);
      setTimeout(() => setSaved(false), 2500);
    },
    onError: (err: Error) => setSaveError(err.message),
  });

  function togglePref(eventType: NotificationEventType) {
    setPrefs((prev) =>
      prev.map((p) => (p.event_type === eventType ? { ...p, enabled: !p.enabled } : p)),
    );
  }

  if (isLoading) return <div className="p-8 text-slate-500 text-sm">Loading…</div>;

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-2xl px-6 py-8">
        <h1 className="mb-6 text-xl font-semibold text-slate-100">Settings</h1>

        <div className="space-y-6">
          {/* Notification email */}
          <section className="rounded-lg border border-aurora bg-surface-panel p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">
              Notification Email
            </h2>
            <p className="mb-3 text-xs text-slate-500">
              Notifications are sent to your login email by default. Enter an alternate address to
              redirect them.
            </p>
            <input
              type="email"
              value={notifEmail}
              onChange={(e) => setNotifEmail(e.target.value)}
              placeholder="Leave blank to use login email"
              className="w-full rounded border border-aurora-strong bg-slate-900 px-3 py-1.5 text-sm text-slate-200 placeholder-slate-600"
            />
          </section>

          {/* Quiet hours */}
          <section className="rounded-lg border border-aurora bg-surface-panel p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">
              Quiet Hours
            </h2>
            <p className="mb-3 text-xs text-slate-500">
              Notifications will be suppressed between these times (based on your time zone).
            </p>
            <div className="flex items-center gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Start</label>
                <input
                  type="time"
                  value={quietStart}
                  onChange={(e) => setQuietStart(e.target.value)}
                  className="rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200"
                />
              </div>
              <span className="text-slate-500 mt-4">to</span>
              <div>
                <label className="block text-xs text-slate-500 mb-1">End</label>
                <input
                  type="time"
                  value={quietEnd}
                  onChange={(e) => setQuietEnd(e.target.value)}
                  className="rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200"
                />
              </div>
              {(quietStart || quietEnd) && (
                <button
                  onClick={() => {
                    setQuietStart('');
                    setQuietEnd('');
                  }}
                  className="mt-4 text-xs text-slate-500 hover:text-slate-300 underline"
                >
                  Clear
                </button>
              )}
            </div>

            <div className="mt-4">
              <label className="block text-xs text-slate-500 mb-1">Time zone</label>
              <select
                value={timeZone}
                onChange={(e) => setTimeZone(e.target.value)}
                className="rounded border border-aurora-strong bg-slate-900 px-2 py-1 text-sm text-slate-200"
              >
                {[
                  'America/Chicago',
                  'America/New_York',
                  'America/Denver',
                  'America/Los_Angeles',
                  'America/Phoenix',
                  'UTC',
                ].map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
          </section>

          {/* Digest mode */}
          <section className="rounded-lg border border-aurora bg-surface-panel p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">
              Digest Mode
            </h2>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={digestMode}
                onChange={(e) => setDigestMode(e.target.checked)}
                className="h-4 w-4 accent-flag-blue"
              />
              <span className="text-sm text-slate-300">
                Batch notifications (suppress duplicates within 60 seconds)
              </span>
            </label>
          </section>

          {/* Per-event preferences */}
          <section className="rounded-lg border border-aurora bg-surface-panel p-5">
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-slate-400">
              Notification Preferences
            </h2>
            <div className="space-y-2">
              {prefs.map((pref) => (
                <label key={pref.event_type} className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pref.enabled}
                    onChange={() => togglePref(pref.event_type)}
                    className="h-4 w-4 accent-flag-blue"
                  />
                  <span className="text-sm text-slate-300">
                    {EVENT_LABELS[pref.event_type] ?? pref.event_type}
                  </span>
                </label>
              ))}
            </div>
          </section>

          <div className="flex items-center gap-3">
            <button
              onClick={() => save.mutate()}
              disabled={save.isPending}
              className="rounded bg-slate-600 px-4 py-2 text-sm text-slate-200 hover:bg-slate-500 disabled:opacity-40"
            >
              {save.isPending ? 'Saving…' : 'Save settings'}
            </button>
            {saved && <span className="text-sm text-green-400">Saved!</span>}
            {saveError && <span className="text-sm text-red-400">Error: {saveError}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}
